import { allowVariablePlaceholders } from '$lib/config-schemas/raw-variable-placeholder';
import { z } from 'zod';

export const OrientationTypes = {
  EULER_ANGLES: 'euler_angles',
  QUATERNION: 'quaternion',
  OV_RADIANS: 'ov_radians',
  OV_DEGREES: 'ov_degrees',
} as const;

const createEulerAnglesSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(OrientationTypes.EULER_ANGLES),
      value: allowVariablePlaceholders(
        z.object({
          roll: z.coerce.number(),
          pitch: z.coerce.number(),
          yaw: z.coerce.number(),
        }),
        allowVariables
      ),
    });
  }
  return z.object({
    type: z.literal(OrientationTypes.EULER_ANGLES),
    value: z.object({
      roll: z.coerce.number().default(0),
      pitch: z.coerce.number().default(0),
      yaw: z.coerce.number().default(0),
    }),
  });
};

export const EulerAnglesSchema = createEulerAnglesSchema();

export type EulerAngles = z.infer<typeof EulerAnglesSchema>;

const createQuaternionSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(OrientationTypes.QUATERNION),
      value: allowVariablePlaceholders(
        z.object({
          x: z.coerce.number(),
          y: z.coerce.number(),
          z: z.coerce.number(),
          w: z.coerce.number(),
        }),
        allowVariables
      ),
    });
  }
  return z.object({
    type: z.literal(OrientationTypes.QUATERNION),
    value: z.object({
      x: z.coerce.number().default(0),
      y: z.coerce.number().default(0),
      z: z.coerce.number().default(0),
      w: z.coerce.number().default(1),
    }),
  });
};

export const QuaternionSchema = createQuaternionSchema();

export type Quaternion = z.infer<typeof QuaternionSchema>;

const createOvRadiansSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(OrientationTypes.OV_RADIANS),
      value: allowVariablePlaceholders(
        z.object({
          x: z.coerce.number(),
          y: z.coerce.number(),
          z: z.coerce.number(),
          th: z.coerce.number(),
        }),
        allowVariables
      ),
    });
  }
  return z.object({
    type: z.literal(OrientationTypes.OV_RADIANS),
    value: z.object({
      x: z.coerce.number().default(0),
      y: z.coerce.number().default(0),
      z: z.coerce.number().default(1),
      th: z.coerce.number().default(0),
    }),
  });
};

export const OvRadiansSchema = createOvRadiansSchema();

export type OrientationVectorRadians = z.infer<typeof OvRadiansSchema>;

const createOvDegreesSchema = (allowVariables = false) => {
  // When variables are allowed, don't apply defaults as they interfere with variable placeholders
  if (allowVariables) {
    return z.object({
      type: z.literal(OrientationTypes.OV_DEGREES),
      value: allowVariablePlaceholders(
        z.object({
          x: z.coerce.number(),
          y: z.coerce.number(),
          z: z.coerce.number(),
          th: z.coerce.number(),
        }),
        allowVariables
      ),
    });
  }
  return z.object({
    type: z.literal(OrientationTypes.OV_DEGREES),
    value: z.object({
      x: z.coerce.number().default(0),
      y: z.coerce.number().default(0),
      z: z.coerce.number().default(1),
      th: z.coerce.number().default(0),
    }),
  });
};

export const OvDegreesSchema = createOvDegreesSchema();

export type OrientationVectorDegrees = z.infer<typeof OvDegreesSchema>;

export const OrientationSchema = z.discriminatedUnion('type', [
  EulerAnglesSchema,
  QuaternionSchema,
  OvRadiansSchema,
  OvDegreesSchema,
]);

export type Orientation = z.infer<typeof OrientationSchema>;
export type OrientationType = Orientation['type'];

export const createOrientationSchema = (allowVariables = false) => {
  return z.discriminatedUnion('type', [
    allowVariablePlaceholders(
      createEulerAnglesSchema(allowVariables),
      allowVariables,
      ['type']
    ),
    allowVariablePlaceholders(
      createQuaternionSchema(allowVariables),
      allowVariables,
      ['type']
    ),
    allowVariablePlaceholders(
      createOvRadiansSchema(allowVariables),
      allowVariables,
      ['type']
    ),
    allowVariablePlaceholders(
      createOvDegreesSchema(allowVariables),
      allowVariables,
      ['type']
    ),
  ]);
};
